import {getTestProps} from '@benchling/js-test-utils';
import classNames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';

import {assert} from 'Asserts';
import {ExtraActionButtons} from 'metadata/FieldActionButtons';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BaseEditableField from 'ui-library/BaseEditableField';
import FormInput from 'ui-library/bs/FormInput';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import LongTextValue from 'ui-library/LongTextValue';
import TextValue from 'ui-library/TextValue';
import UrlAwareText from 'ui-library/UrlAwareText';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

// Must be a subset of FormInputType
export enum TextFieldType {
  TEXT = 'text',
  NUMBER = 'number',
  TEXTAREA = 'textarea',
}

export interface EditableTextFieldProps {
  isFullWidth?: boolean;
  inputClassName?: string;
  isDisabled: boolean;
  emptyValueAllowed: boolean;
  initialIsBeingEdited: boolean;
  placeholder?: string;
  numRows?: number;
  // Callback that is fired when change is completed. Return true if change is unsuccessful due to synchronous
  // validation and should be blocked.
  requestChange: (changedValue: string | number) => boolean | void | Promise<void>;
  type: TextFieldType;
  urlAware?: boolean;
  textPlaceholder?: string;
  // Allow using component for strings and numbers -- this can be revisited with reusable field inputs.
  value: string | number;
  onIsBeingEditedChanged: (isBeingEdited: boolean) => void;
  dataTestAttrs?: DataTestAttrs;
  // Wait for confirmation from this function before entering edit mode
  confirmBeforeStartEditing?: () => Promise<boolean>;
  // Disable and render provided tooltip if non-null
  disableEditsWithTooltip?: string | null;
  // Sets a character limit on the text
  maxLength?: number;
  /** Specifies the right-aligned component next to the editor within the field (defaults to action buttons) */
  editorSiblingComponent?: JSX.Element;
  extraActionButtons?: ExtraActionButtons;
  hasFieldActionButtonsOuterMargin?: boolean;
  viewerClassName?: string;
  breakLines?: boolean;
  /** The ID attribute of the element labelling this field */
  labelledBy?: string;
}

export default function EditableTextField(props: EditableTextFieldProps): JSX.Element {
  const [editingValue, setEditingValue] = useState(props.value);
  const [isBeingEdited, setIsBeingEdited] = useState(props.initialIsBeingEdited);
  const isDisabled = props.isDisabled || props.disableEditsWithTooltip != null;

  function toggleEdit(toggleTo: boolean): void {
    setEditingValue(props.value);
    setIsBeingEdited(toggleTo);
    props.onIsBeingEditedChanged(toggleTo);
  }

  const formInputRef = useRef<FormInput>(null);

  useEffect(() => {
    if (isBeingEdited) {
      formInputRef.current?.inputRef.current?.focus();
    }
  }, [isBeingEdited]);

  function handleChangeValue(e: InputChangeEvent): void {
    setEditingValue(e.target.value);
  }

  function handleComplete(): void {
    const shouldBlockOrPromise = props.requestChange(editingValue);
    if (shouldBlockOrPromise instanceof Promise || !shouldBlockOrPromise) {
      toggleEdit(false);
    }
  }

  function handleEdit(): void {
    toggleEdit(true);
  }

  async function handleEditWithConfirmation(): Promise<void> {
    assert(props.confirmBeforeStartEditing != null, 'Expected confirmBeforeStartEditing to be provided');
    const shouldProceed = await props.confirmBeforeStartEditing();
    if (shouldProceed) {
      toggleEdit(true);
    }
  }

  function isValueValid(): boolean {
    return Boolean(props.emptyValueAllowed || editingValue);
  }

  function isEnterValidSubmission(): boolean {
    return TextFieldType.TEXTAREA !== props.type;
  }

  function handleCancel(): void {
    toggleEdit(false);
  }

  function handleKeyDown(e: ElementKeyboardEvent): void {
    if (keyboard.isEnterKey(e)) {
      if (!isEnterValidSubmission()) {
        return;
      }
      e.preventDefault();
      if (isValueValid()) {
        handleComplete();
      }
    } else if (keyboard.isEscapeKey(e)) {
      e.preventDefault();
      handleCancel();
    }
  }

  const editableField = (
    <BaseEditableField
      editor={
        <FormInput
          ref={formInputRef}
          className={classNames('u-flex-grow', props.inputClassName)}
          disabled={isDisabled}
          onChange={handleChangeValue}
          onKeyDown={handleKeyDown}
          placeholder={props.placeholder}
          selectAllOnClick={false}
          focusOnMount={true}
          type={props.type}
          value={editingValue}
          rows={props.numRows}
          maxLength={props.maxLength}
        />
      }
      isBeingEdited={isBeingEdited}
      isCompleteEnabled={isValueValid()}
      isWritable={!isDisabled}
      labelledBy={props.labelledBy}
      viewer={
        props.type === TextFieldType.TEXTAREA ? (
          <div
            className={classNames(
              'editableField-text',
              props.breakLines ? 'editableField-text-multiline' : undefined,
              'u-pre-line',
              props.viewerClassName
            )}
            {...getTestProps({component: 'EditableTextField', element: 'viewer'})}
          >
            {props.urlAware ? (
              <UrlAwareText textValue={props.value as string} placeholder={props.textPlaceholder} />
            ) : (
              <LongTextValue value={props.value as string} placeholder={props.textPlaceholder} />
            )}
          </div>
        ) : (
          <div
            className={classNames(
              'editableField-text',
              !props.breakLines ? 'u-ellipsis-text' : undefined,
              props.viewerClassName
            )}
            {...getTestProps({component: 'EditableTextField', element: 'viewer'})}
          >
            <TextValue
              value={props.value}
              placeholder={props.textPlaceholder}
              breakLines={props.breakLines}
            />
          </div>
        )
      }
      onCancel={handleCancel}
      onComplete={handleComplete}
      onEdit={props.confirmBeforeStartEditing != null ? handleEditWithConfirmation : handleEdit}
      dataTestAttrs={props.dataTestAttrs}
      isFullWidth={props.isFullWidth}
      editorSiblingComponent={props.editorSiblingComponent}
      extraActionButtons={props.extraActionButtons}
      hasFieldActionButtonsOuterMargin={props.hasFieldActionButtonsOuterMargin}
    />
  );
  return (
    <Tooltip text={props.disableEditsWithTooltip} isDisabled={!props.disableEditsWithTooltip}>
      <div>{editableField}</div>
    </Tooltip>
  );
}

EditableTextField.defaultProps = {
  emptyValueAllowed: false,
  initialIsBeingEdited: false,
  isDisabled: false,
  type: TextFieldType.TEXT,
  urlAware: false,
  onIsBeingEditedChanged: () => {},
  numRows: 3,
  isFullWidth: false,
  hasFieldActionButtonsOuterMargin: true,
};
